'use client'

import { PaginationModel } from '@/core/types/models/pagination.model'
import { Skeleton } from '@/core/ui/atoms/skeleton'
import { InfiniteFetch } from '@/core/ui/components/infinite-fetch'
import { ProductFilters } from '@/features/product/model/product-filters'
import { Product } from '@/features/product/model/product.model'
import { getProducts } from '@/features/product/providers/server'
import { ProductCard } from '@/features/product/ui'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'
import { SortByFilter } from './sort-by-filter'
import styles from './ProductsGrid.module.css'

type Props = {
  initialProducts: Product[]
  initialPagination: PaginationModel
  filters: ProductFilters
}

export function SearchProducts({
  initialProducts,
  initialPagination,
  filters,
}: Props) {
  const lastPage = initialPagination.isLast
  const [products, setProducts] = useState(initialProducts)
  const [page, setPage] = useState(initialPagination.currentPage)
  const [hasNextPage, setHasNextPage] = useState(lastPage ? false : true)
  const [loading, setLoading] = useState(false)
  const t = useTranslations()

  useEffect(() => {
    setProducts(initialProducts)
    setPage(initialPagination.currentPage)
    setHasNextPage(!initialPagination.isLast)
  }, [initialProducts, initialPagination])

  const loadMore = useCallback(async () => {
    if (loading || !hasNextPage) return

    setLoading(true)

    const nextPage = page + 1

    const { products: newProducts, pagination } = await getProducts({
      ...filters,
      page: nextPage,
    })

    setProducts(prev => [...prev, ...newProducts])
    setPage(nextPage)
    setHasNextPage(pagination.lastPage ? false : true)
    setLoading(false)
  }, [filters, page, hasNextPage, loading])

  return (
    <div className='h-full'>
      <div className='mb-4 flex flex-col items-start justify-between gap-5 md:flex-row md:items-baseline'>
        <p className='text-mountain-bluebird mb-1 whitespace-nowrap sm:mb-0'>
          {t('search.Showing')} {products.length} {t('search.items')}
        </p>
        <SortByFilter />
      </div>
      
      
<div className={styles.productsGrid}>
  {products.map(product => (
    <ProductCard key={product.id} {...product} />
  ))}
</div>

      <div>
        {hasNextPage && (
          <InfiniteFetch onActive={loadMore} rootMargin='200px'>
            {!loading && (
              <div className='mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5'>
                {Array.from({ length: 10 }).map((_, i) => (
                  <div
                    key={i}
                    className='animate-pulse space-y-3 rounded-xl bg-gray-100 p-3'
                  >
                    <Skeleton height='128px' borderRadius='8px' />
                    <Skeleton height='16px' />
                    <Skeleton height='16px' width='50%' />
                  </div>
                ))}
              </div>
            )}
          </InfiniteFetch>
        )}
      </div>
    </div>
  )
}
